import { memo, useEffect, useRef, useState } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import { useStore, NET_TERMINAL_HANDLE, type PowerSymbolNode as TPowerSymbolNode } from '../store';

/**
 * Power / ground symbol. A node with a single terminal handle that joins its
 * wired pin into a named net. Power symbols are GLOBAL — every symbol sharing
 * a name (e.g. "GND") is the same net across all sheets.
 */
function PowerSymbolNodeImpl({ id, data, selected }: NodeProps<TPowerSymbolNode>) {
  const setNetNodeName = useStore((s) => s.setNetNodeName);
  const [editing, setEditing] = useState(false);
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) {
      ref.current?.focus();
      ref.current?.select();
    }
  }, [editing]);

  const ground = data.symbol === 'gnd' || data.symbol === 'earth';
  const accent = ground ? '#475569' : '#b45309';

  return (
    <div
      onDoubleClick={(e) => {
        e.stopPropagation();
        setEditing(true);
      }}
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        padding: 4,
        cursor: editing ? 'text' : 'move',
        filter: selected ? 'drop-shadow(0 0 0 2px var(--accent))' : 'none',
      }}
    >
      {/* terminal handle (top) — both directions, single id */}
      <Handle type="source" id={NET_TERMINAL_HANDLE} position={Position.Top} isConnectableStart isConnectableEnd style={handleStyle} />
      <Handle type="target" id={NET_TERMINAL_HANDLE} position={Position.Top} isConnectableStart isConnectableEnd style={{ ...handleStyle, opacity: 0 }} />

      <svg width="34" height="30" viewBox="0 0 34 30" style={{ overflow: 'visible' }}>
        {/* stub from the handle down */}
        <line x1="17" y1="0" x2="17" y2="12" stroke={accent} strokeWidth="2" />
        {ground ? (
          <>
            <line x1="6" y1="13" x2="28" y2="13" stroke={accent} strokeWidth="2.4" />
            <line x1="10" y1="18" x2="24" y2="18" stroke={accent} strokeWidth="2.4" />
            <line x1="14" y1="23" x2="20" y2="23" stroke={accent} strokeWidth="2.4" />
          </>
        ) : (
          // VCC / rail: bar + up-stub
          <line x1="6" y1="12" x2="28" y2="12" stroke={accent} strokeWidth="2.6" />
        )}
      </svg>

      {editing ? (
        <input
          ref={ref}
          defaultValue={data.name}
          onBlur={(e) => {
            setNetNodeName(id, e.target.value.trim() || data.name);
            setEditing(false);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
            else if (e.key === 'Escape') setEditing(false);
          }}
          style={nameInputStyle}
        />
      ) : (
        <span style={{ ...nameStyle, color: accent }}>{data.name}</span>
      )}
    </div>
  );
}

const handleStyle = {
  width: 9,
  height: 9,
  background: '#fff',
  border: '2px solid #64748b',
  borderRadius: 999,
};
const nameStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 800,
  letterSpacing: 0.3,
  fontFamily: 'ui-monospace, Consolas, monospace',
  whiteSpace: 'nowrap',
};
const nameInputStyle: React.CSSProperties = {
  width: 70,
  fontSize: 10,
  fontWeight: 800,
  textAlign: 'center',
  border: '1px solid var(--accent)',
  borderRadius: 3,
  outline: 'none',
  fontFamily: 'ui-monospace, Consolas, monospace',
};

export const PowerSymbolNode = memo(PowerSymbolNodeImpl);
